import styled from '@emotion/styled';
import { MessageTemplateArgumentDataType } from '@racemap/sdk/schema/messageTemplates';
import type { TRACKER_TYPES } from '@racemap/sdk/schema/trackers';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { capitalize } from '@racemap/utilities/functions/utils';
import { Form, type FormInstance, Input, Select, Space, Tag } from 'antd';
import type { Immutable } from 'immer';
import type { FC } from 'react';
import React from 'react';
import type { ManagedMessageTemplateArgument } from '../../../store/trackers/trackers_reducers';
import { Devider } from '../../BasicComponents/Devider/Devider';
import type { FormValues, Parameter } from './AddTrackerCommando';

interface Props {
  templateArguments: Immutable<Array<ManagedMessageTemplateArgument>>;
  form: FormInstance<FormValues>;
  relevantTrackerTypes: Array<TRACKER_TYPES>;
}

export const TrackerCommandArgumentsForm: FC<Props> = ({
  templateArguments,
  form,
  relevantTrackerTypes,
}) => {
  const args = form.getFieldValue('parameters') as Array<Parameter>;

  return (
    <>
      {args.length > 0 && (
        <>
          <Devider />
          <Form.Item label="Arguments:" style={{ marginBottom: 5 }} />
        </>
      )}

      <Form.List name="parameters">
        {(fields) => (
          <>
            {fields.map(({ name, ...field }, index) => {
              const parameter: Parameter = form.getFieldValue(['parameters', name]);
              const templateArgument = templateArguments.find((a) =>
                a.id.equals(parameter.argumentId),
              );

              return (
                <React.Fragment key={name}>
                  <TrackerTypes>
                    <div className="prefix">For:</div>
                    {templateArgument?.trackerTypes
                      ?.filter((t) => relevantTrackerTypes.includes(t))
                      .map((t) => (
                        <Tag key={t}>{t}</Tag>
                      ))}
                  </TrackerTypes>
                  <Form.Item
                    {...field}
                    labelCol={{ span: 8 }}
                    wrapperCol={{ span: 16 }}
                    label={capitalize(templateArgument?.name || 'Unknown')}
                    name={[name, 'value']}
                    extra={<Description>{templateArgument?.description}</Description>}
                  >
                    {templateArgument?.dataType === MessageTemplateArgumentDataType.SELECT ? (
                      <Select>
                        {templateArgument.options?.map((o) => (
                          <Select.Option key={o.key} value={o.value}>
                            {o.label}
                          </Select.Option>
                        ))}
                      </Select>
                    ) : (
                      <Input type={templateArgument?.dataType} />
                    )}
                  </Form.Item>
                  {index !== fields.length - 1 && <Devider />}
                </React.Fragment>
              );
            })}
          </>
        )}
      </Form.List>
    </>
  );
};

const TrackerTypes = styled(Space)`
  margin-bottom: 10px;
  width: 100%;
  justify-content: flex-end;
  flex-wrap: wrap;
  row-gap: 4px;
  column-gap: 0px;

  .prefix {
    margin-right: 5px;
    font-weight: 500;
  }
`;

const Description = styled.div`
  color: ${RacemapColors.DarkGray};
`;
